import { useEffect, useRef, useState } from 'react';
import { asset } from '../config';
import { fitPointBudget } from '../design/geo';
import { readRouteFiles } from '../design/gpx';
import {
  FRAME_COLOURS,
  FRAME_COLOUR_SWATCH,
  FRAMINGS,
  SIZES,
  SIZE_LABELS,
  isAvailable,
  normaliseProduct,
  type Product,
} from '../design/products';
import { LIMITS, uid, type Design, type Label, type LabelSide, type Route, type StatsMode } from '../design/schema';
import { routeSummary, statsLine, totals } from '../design/stats';
import { STYLES, getStyle } from '../design/styles';
import { track } from '../services/analytics';
import { formatPrice, type Price } from '../services/shopify';
import { IconDown, IconPlus, IconTrash, IconUp, IconUpload } from './icons';
import { useEditor, type Editor } from './store';
import { Field, Modal, Segmented, TextField, Toggle } from './ui';

// ---------------------------------------------------------------------------
// Adding routes (shared by the start screen and the Route tab)
// ---------------------------------------------------------------------------

/** Keep the whole design's drawn line to a size that saves and loads quickly, however much is uploaded. */
export function withinPointBudget(routes: Route[]): Route[] {
  const fitted = fitPointBudget(routes.map((r) => r.coords));
  return routes.map((r, i) => (fitted[i].length === r.coords.length ? r : { ...r, coords: fitted[i] }));
}

export function useAddRoutes({ edit, dispatch, state }: Editor) {
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const addRoutes = (routes: Route[]) => {
    if (!routes.length) return;
    const first = state.design.routes.length === 0;
    // New geometry: go back to the auto-fitted view so the whole route is visible.
    edit((d) => ({ ...d, routes: withinPointBudget([...d.routes, ...routes]), camera: null }));
    if (first) dispatch({ type: 'tab', tab: 'style' });
    track('designer_route_added', { source: routes[0].source, routes: routes.length });
  };

  const addFiles = async (fileList: Iterable<File>) => {
    // Copy first: a file input's FileList empties as soon as the input is reset.
    const files = Array.from(fileList);
    setBusy(true);
    const result = await readRouteFiles(files);
    setBusy(false);
    setErrors(result.errors);
    addRoutes(result.routes);
  };

  return { addFiles, addRoutes, errors, busy, clearErrors: () => setErrors([]) };
}

export function UploadBox({ compact, adder }: { compact?: boolean; adder: ReturnType<typeof useAddRoutes> }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`tmd-drop${compact ? ' is-compact' : ''}${over ? ' is-over' : ''}`}
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          adder.addFiles(e.dataTransfer.files);
        }}
      >
        <IconUpload />
        <strong>{adder.busy ? 'Reading…' : compact ? 'Add another file' : 'Upload GPX file(s)'}</strong>
        {!compact && <span>Drop files here or tap to browse. GPX, TCX or KML.</span>}
      </button>
      <input
        ref={input}
        type="file"
        accept=".gpx,.tcx,.kml,application/gpx+xml"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) adder.addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      {adder.errors.length > 0 && (
        <div className="tmd-alert" role="alert">
          {adder.errors.map((m) => (
            <p key={m}>{m}</p>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Start (no route yet)
// ---------------------------------------------------------------------------

export function StartPanel({ draft, adder, onResume, onStrava, onSample }: {
  draft: { design: Design; savedAt: number } | null;
  adder: ReturnType<typeof useAddRoutes>;
  onResume: () => void;
  onStrava: (() => void) | null;
  onSample: () => void;
}) {
  return (
    <div className="tmd-panel tmd-start">
      <p className="tmd-eyebrow">Step 1</p>
      <h2>Add your route</h2>
      <p className="tmd-lede">Turn a ride, run or hike into a print. Start from the file your watch or bike computer recorded.</p>

      {draft && (
        <button type="button" className="tmd-resume" onClick={onResume}>
          <span>
            <strong>Continue your design</strong>
            <em>
              {draft.design.text.title || draft.design.routes[0]?.name || 'Untitled'} · saved {timeAgo(draft.savedAt)}
            </em>
          </span>
          <span aria-hidden="true">→</span>
        </button>
      )}

      <UploadBox adder={adder} />

      {onStrava && (
        <button type="button" className="tmd-btn tmd-btn-strava" onClick={onStrava}>
          Connect with Strava
        </button>
      )}

      <p className="tmd-muted">
        No file to hand?{' '}
        <button type="button" className="tmd-linkbtn" onClick={onSample}>
          Try it with a sample route
        </button>
        .{' '}
        <a href="https://trailmaps.com/pages/help-with-the-design-your-own-map-builder" target="_blank" rel="noreferrer">
          How do I get a GPX file?
        </a>
      </p>
    </div>
  );
}

function timeAgo(ts: number) {
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export function RoutePanel({ adder, onStrava, onRestart }: { adder: ReturnType<typeof useAddRoutes>; onStrava: (() => void) | null; onRestart: () => void }) {
  const { state, edit } = useEditor();
  const { routes } = state.design;
  const t = totals(routes);

  const move = (i: number, by: number) =>
    edit((d) => {
      const next = [...d.routes];
      const [r] = next.splice(i, 1);
      next.splice(i + by, 0, r);
      return { ...d, routes: next };
    });
  const remove = (id: string) => edit((d) => ({ ...d, routes: d.routes.filter((r) => r.id !== id), camera: null }));

  return (
    <div className="tmd-panel">
      <h2>Your route</h2>
      <p className="tmd-muted">
        {routes.length > 1
          ? 'Routes are joined in this order for the distance and elevation profile.'
          : 'Add more files to combine several days or stages into one map.'}
      </p>
      <ul className="tmd-routes">
        {routes.map((r, i) => (
          <li key={r.id}>
            <span className="tmd-route-n">{i + 1}</span>
            <span className="tmd-route-main">
              <strong>{r.name}</strong>
              <em>{routeSummary(r)}</em>
            </span>
            {routes.length > 1 && (
              <>
                <button type="button" className="tmd-icon-btn" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move ${r.name} up`}>
                  <IconUp />
                </button>
                <button type="button" className="tmd-icon-btn" disabled={i === routes.length - 1} onClick={() => move(i, 1)} aria-label={`Move ${r.name} down`}>
                  <IconDown />
                </button>
              </>
            )}
            <button type="button" className="tmd-icon-btn" onClick={() => remove(r.id)} aria-label={`Remove ${r.name}`}>
              <IconTrash />
            </button>
          </li>
        ))}
      </ul>
      {routes.length > 1 && (
        <p className="tmd-total">
          Total {(t.distance / 1000).toFixed(1)} km · {Math.round(t.gain).toLocaleString('en-GB')} m climbing
        </p>
      )}
      <UploadBox compact adder={adder} />
      {onStrava && (
        <button type="button" className="tmd-btn tmd-btn-ghost" onClick={onStrava}>
          Add from Strava
        </button>
      )}
      <hr />
      <button type="button" className="tmd-linkbtn" onClick={onRestart}>
        Start a new design
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Style
// ---------------------------------------------------------------------------

// The route colours offered, in this order (plus "Auto", the style's own colour).
const LINE_COLOURS = ['#408F2A', '#BCCF00', '#F0E400', '#F6A000', '#DB2C4D', '#E83B84', '#703A85', '#0067B2', '#00B5DB', '#50B79F'];

export function StylePanel() {
  const { state, edit } = useEditor();
  const d = state.design;
  const style = getStyle(d.style);
  return (
    <div className="tmd-panel">
      <h2>Map style</h2>
      <div className="tmd-styles" role="radiogroup" aria-label="Map style">
        {STYLES.map((s) => (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={s.id === d.style}
            className={s.id === d.style ? 'is-on' : ''}
            onClick={() => edit((x) => ({ ...x, style: s.id }))}
          >
            <img src={asset(s.swatch)} alt="" loading="lazy" />
            <span>{s.name}</span>
          </button>
        ))}
      </div>

      <h3>Route line</h3>
      <Field label="Thickness" hint={`${d.line.width.toFixed(1)}`}>
        {(id) => (
          <input
            id={id}
            className="tmd-range"
            type="range"
            min={LIMITS.lineMin}
            max={LIMITS.lineMax}
            step={0.5}
            value={d.line.width}
            onChange={(e) => edit((x) => ({ ...x, line: { ...x.line, width: Number(e.target.value) } }), 'line-width')}
          />
        )}
      </Field>
      <Field label="Colour">
        {() => (
          <div className="tmd-swatches is-line">
            <button
              type="button"
              className={`tmd-swatch is-auto${d.line.color == null ? ' is-on' : ''}`}
              style={{ background: style.route }}
              title={`Matched to ${style.name}`}
              onClick={() => edit((x) => ({ ...x, line: { ...x.line, color: null } }))}
            >
              <span>Auto</span>
            </button>
            {LINE_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                className={`tmd-swatch${d.line.color?.toUpperCase() === c ? ' is-on' : ''}`}
                style={{ background: c }}
                aria-label={`Line colour ${c}`}
                onClick={() => edit((x) => ({ ...x, line: { ...x.line, color: c } }))}
              />
            ))}
          </div>
        )}
      </Field>
      <Toggle checked={d.line.endpoints} onChange={(v) => edit((x) => ({ ...x, line: { ...x.line, endpoints: v } }))}>
        Mark start and finish
      </Toggle>
      <Toggle checked={d.elevation} onChange={(v) => edit((x) => ({ ...x, elevation: v }))}>
        Elevation profile
      </Toggle>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

export function TextPanel() {
  const { state, edit } = useEditor();
  const d = state.design;
  const setText = (key: keyof Design['text'], value: string | boolean) =>
    edit((x) => ({ ...x, text: { ...x.text, [key]: value } }), `text-${key}`);
  const t = totals(d.routes);
  const auto = statsLine({ routes: d.routes, text: { ...d.text, statsMode: d.text.statsMode === 'custom' ? 'metric' : d.text.statsMode } });

  return (
    <div className="tmd-panel">
      <h2>Your text</h2>
      <TextField label="Title" value={d.text.title} onChange={(v) => setText('title', v)} placeholder="e.g. Coast to Coast" maxLength={LIMITS.title} />
      <TextField label="Location or sub-title" value={d.text.subtitle} onChange={(v) => setText('subtitle', v)} placeholder="e.g. Lake District, England" maxLength={LIMITS.subtitle} />

      <Field label="Stats line">
        {() => (
          <Segmented<StatsMode>
            label="Stats units"
            value={d.text.statsMode}
            onChange={(mode) =>
              edit((x) => ({
                ...x,
                // Switching to custom starts from the automatic text rather than a blank box.
                text: { ...x.text, statsMode: mode, stats: mode === 'custom' && !x.text.stats ? auto : x.text.stats },
              }))
            }
            options={[
              { value: 'metric', label: 'KM & metres' },
              { value: 'imperial', label: 'Miles & feet' },
              { value: 'custom', label: 'Custom' },
            ]}
          />
        )}
      </Field>
      {d.text.statsMode === 'custom' ? (
        <TextField label="Custom stats" value={d.text.stats} onChange={(v) => setText('stats', v)} placeholder="434 MILES // 6,000 FEET" maxLength={LIMITS.stats} />
      ) : (
        <>
          <p className="tmd-readout">{auto || '—'}</p>
          {t.movingTime != null && (
            <Toggle checked={d.text.statsTime} onChange={(v) => setText('statsTime', v)}>
              Include moving time
            </Toggle>
          )}
        </>
      )}

      <h3>Small print</h3>
      <TextField label="Bottom left" value={d.text.left} onChange={(v) => setText('left', v)} placeholder="e.g. 14 June 2026" maxLength={LIMITS.footer} />
      <TextField label="Bottom right" value={d.text.right} onChange={(v) => setText('right', v)} placeholder="e.g. Ridden with Dad" maxLength={LIMITS.footer} />
      <p className="tmd-muted">Leave any line empty and it simply won't be printed.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

const SIDES: Array<{ value: LabelSide; label: string; title: string }> = [
  { value: 'center', label: 'Aa', title: 'Text only' },
  { value: 'left', label: '←', title: 'Point marker, text to its left' },
  { value: 'top', label: '↑', title: 'Point marker, text above' },
  { value: 'bottom', label: '↓', title: 'Point marker, text below' },
  { value: 'right', label: '→', title: 'Point marker, text to its right' },
];

export function LabelsPanel() {
  const { state, edit, dispatch } = useEditor();
  const { labels } = state.design;
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const full = labels.length >= LIMITS.labels;

  // Selecting a label on the map (or creating one) puts the cursor in its text box.
  useEffect(() => {
    if (!state.selectedLabel) return;
    const el = inputs.current.get(state.selectedLabel);
    if (el && document.activeElement !== el) {
      el.focus({ preventScroll: false });
      el.select();
    }
  }, [state.selectedLabel]);

  const patch = (id: string, change: Partial<Label>, key?: string) =>
    edit((d) => ({ ...d, labels: d.labels.map((l) => (l.id === id ? { ...l, ...change } : l)) }), key);
  const remove = (id: string) => {
    edit((d) => ({ ...d, labels: d.labels.filter((l) => l.id !== id) }));
    if (state.selectedLabel === id) dispatch({ type: 'selectLabel', id: null });
  };

  return (
    <div className="tmd-panel">
      <h2>Labels</h2>
      <p className="tmd-muted">Pin names to places on your map: a summit, a café stop, where you camped. Drag a label on the map to move it.</p>
      {state.placing ? (
        <div className="tmd-placing" role="status">
          <span>Tap the map where the label should go</span>
          <button type="button" className="tmd-linkbtn" onClick={() => dispatch({ type: 'placing', on: false })}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" className="tmd-btn tmd-btn-ghost" disabled={full} onClick={() => dispatch({ type: 'placing', on: true })}>
          <IconPlus /> Add a label
        </button>
      )}
      {full && <p className="tmd-muted">That's the maximum of {LIMITS.labels} labels.</p>}

      <ul className="tmd-labels">
        {labels.map((l, i) => (
          <li key={l.id} className={l.id === state.selectedLabel ? 'is-selected' : ''}>
            <div className="tmd-label-row">
              <span className="tmd-route-n">{i + 1}</span>
              <input
                ref={(el) => {
                  if (el) inputs.current.set(l.id, el);
                  else inputs.current.delete(l.id);
                }}
                className="tmd-input"
                type="text"
                value={l.text}
                maxLength={LIMITS.label}
                placeholder="Name of a place or stop"
                aria-label={`Label ${i + 1} text`}
                onFocus={() => state.selectedLabel !== l.id && dispatch({ type: 'selectLabel', id: l.id })}
                onChange={(e) => patch(l.id, { text: e.target.value }, `label-text-${l.id}`)}
              />
              <button type="button" className="tmd-icon-btn" onClick={() => remove(l.id)} aria-label={`Delete label ${i + 1}`}>
                <IconTrash />
              </button>
            </div>
            {l.id === state.selectedLabel && (
              <Segmented<LabelSide>
                label="Text position"
                value={l.dot ? l.side : 'center'}
                onChange={(side) => patch(l.id, side === 'center' ? { dot: false, side: 'center' } : { dot: true, side })}
                options={SIDES}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// New labels start in v1's style (plain text on the spot); a point marker is an option per label.
export const newLabel = (lng: number, lat: number): Label => ({ id: uid(), lng, lat, text: '', side: 'center', dot: false });

// ---------------------------------------------------------------------------
// Map position
// ---------------------------------------------------------------------------

export function MapPanel({ onZoom, onFit, onRotate }: { onZoom: (delta: number) => void; onFit: () => void; onRotate: (bearing: number) => void }) {
  const { state } = useEditor();
  const d = state.design;
  const bearing = d.camera?.bearing ?? 0;
  return (
    <div className="tmd-panel">
      <h2>Position</h2>
      <p className="tmd-muted">Drag the map to move it. Pinch or scroll to zoom. What you see inside the frame is exactly what is printed.</p>
      <div className="tmd-btn-row">
        <button type="button" className="tmd-btn tmd-btn-ghost" onClick={() => onZoom(0.25)}>
          Zoom in
        </button>
        <button type="button" className="tmd-btn tmd-btn-ghost" onClick={() => onZoom(-0.25)}>
          Zoom out
        </button>
      </div>
      <Field label="Rotate" hint={`${Math.round(bearing)}°`}>
        {(id) => (
          <input
            id={id}
            className="tmd-range"
            type="range"
            min={-180}
            max={180}
            step={1}
            value={bearing}
            // Snap near north so it is easy to get back to an upright map.
            onChange={(e) => onRotate(Math.abs(Number(e.target.value)) < 4 ? 0 : Number(e.target.value))}
          />
        )}
      </Field>
      <button type="button" className="tmd-btn tmd-btn-ghost" onClick={onFit} disabled={d.camera == null}>
        Re-centre on the whole route
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Size & framing
// ---------------------------------------------------------------------------

export function SizePanel({ price }: { price: Price }) {
  const { state, edit } = useEditor();
  const p = state.design.product;
  const [guide, setGuide] = useState(false);
  const set = (change: Partial<Product>) => edit((d) => ({ ...d, product: normaliseProduct({ ...d.product, ...change }) }));

  return (
    <div className="tmd-panel">
      <h2>Size &amp; framing</h2>
      <Field
        label="Size"
        hint={
          <button type="button" className="tmd-linkbtn" onClick={() => setGuide(true)}>
            Size guide
          </button>
        }
      >
        {() => (
          <div className="tmd-cards">
            {SIZES.map((s) => (
              <button key={s} type="button" className={s === p.size ? 'is-on' : ''} aria-pressed={s === p.size} onClick={() => set({ size: s })}>
                <strong>{SIZE_LABELS[s].short}″</strong>
                <span>{SIZE_LABELS[s].cm}</span>
              </button>
            ))}
          </div>
        )}
      </Field>
      <Field label="Framing">
        {() => (
          <div className="tmd-options">
            {FRAMINGS.map((f) => {
              const ok = isAvailable(p.size, f);
              return (
                <button key={f} type="button" disabled={!ok} className={f === p.framing ? 'is-on' : ''} aria-pressed={f === p.framing} onClick={() => set({ framing: f })}>
                  <span>{f}</span>
                  {!ok && <em>Not available at {SIZE_LABELS[p.size].short}″</em>}
                </button>
              );
            })}
          </div>
        )}
      </Field>
      {p.framing !== 'Unframed' && (
        <Field label="Frame colour" hint={p.frameColour}>
          {() => (
            <div className="tmd-swatches">
              {FRAME_COLOURS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`tmd-swatch is-large${c === p.frameColour ? ' is-on' : ''}`}
                  style={{ background: FRAME_COLOUR_SWATCH[c] }}
                  aria-label={c}
                  aria-pressed={c === p.frameColour}
                  title={c}
                  onClick={() => set({ frameColour: c })}
                />
              ))}
            </div>
          )}
        </Field>
      )}
      <p className="tmd-total">{formatPrice(price.amount)} · free worldwide shipping</p>
      {guide && (
        <Modal title="Size guide" onClose={() => setGuide(false)} wide>
          <img className="tmd-guide" src={asset('img/frame-sizes.jpg')} alt="Print sizes shown to scale on a wall" />
        </Modal>
      )}
    </div>
  );
}
